import {
  type OrderType as BlotterOrderType,
  isAlgoOrderType,
} from '@/store/blotter/api/types/BlotterOrderEntryFromBackend.ts';
import { blotterSlice } from '@/store/blotter/slice.ts';
import type { BlotterOrderEntry } from '@/store/blotter/types/BlotterOrderEntry.ts';
import { OPTIMISTIC_UPDATE_TIME } from '@/store/blotter/types/convertTradesForFront.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import type { OptionSide } from '@/store/option/state.ts';
import { algoOrderApi } from '@/store/orders/algoOrders/api/index.ts';
import type { AlgoOrderRequest } from '@/store/orders/algoOrders/state.ts';
import { callOrderApi } from '@/store/orders/call/api/index.ts';
import type { SubmitCallOrderRequest } from '@/store/orders/call/state.ts';
import { fixingOrderApi } from '@/store/orders/fixing/api/index.ts';
import type { SubmitFixingOrderRequest } from '@/store/orders/fixing/state.ts';
import type { SubmitOrderResponse } from '@/store/orders/state.ts';
import { stopLossOrderApi } from '@/store/orders/stopLoss/api/index.ts';
import type { SubmitStopLossOrderRequest } from '@/store/orders/stopLoss/state.ts';
import { takeProfitOrderApi } from '@/store/orders/takeProfitOrder/api/index.ts';
import type { SubmitTakeProfitRequest } from '@/store/orders/takeProfitOrder/api/submitTakeProfit.ts';
import type { ClientWay } from '@/store/orders/types.ts';
import type { AppDispatch } from '@/store/store.ts';

const BLOTTER_ORDER_TYPE_BY_ALGO_ORDER_TYPE: Record<AlgoOrderRequest['orderType'], BlotterOrderType> = {
  'Twap+Order': 'Twap',
  FalconOrder: 'Falcon',
  NightjarOrder: 'Nightjar',
};

type SubmitOrderArgs =
  | SubmitTakeProfitRequest
  | SubmitStopLossOrderRequest
  | SubmitCallOrderRequest
  | SubmitFixingOrderRequest
  | AlgoOrderRequest;

type SubmitOrderArgKey = SubmitOrderArgs extends infer A ? (A extends object ? keyof A : never) : never;

/**
 * Inserts a placeholder blotter entry as soon as the submission is acknowledged by OMS, so the
 * executed tile can render without waiting for the SignalR order notification.
 */
export const registerOptimisticOrderListener = (startAppListening: StartAppListening) => {
  startAppListening({
    matcher: takeProfitOrderApi.endpoints.submitTakeProfit.matchFulfilled,
    effect: (action, api) => {
      upsert(api.dispatch, action.payload, 'TakeProfit', action.meta.arg.originalArgs);
    },
  });

  startAppListening({
    matcher: stopLossOrderApi.endpoints.submitStopLoss.matchFulfilled,
    effect: (action, api) => {
      upsert(api.dispatch, action.payload, 'StopLoss', action.meta.arg.originalArgs);
    },
  });

  startAppListening({
    matcher: callOrderApi.endpoints.submitCallOrder.matchFulfilled,
    effect: (action, api) => {
      upsert(api.dispatch, action.payload, 'Call', action.meta.arg.originalArgs);
    },
  });

  startAppListening({
    matcher: fixingOrderApi.endpoints.submitFixing.matchFulfilled,
    effect: (action, api) => {
      upsert(api.dispatch, action.payload, 'Fixing', action.meta.arg.originalArgs);
    },
  });

  startAppListening({
    matcher: algoOrderApi.endpoints.submitAlgo.matchFulfilled,
    effect: (action, api) => {
      const args = action.meta.arg.originalArgs;
      upsert(api.dispatch, action.payload, BLOTTER_ORDER_TYPE_BY_ALGO_ORDER_TYPE[args.orderType], args);
    },
  });
};

function upsert(
  dispatch: AppDispatch,
  response: SubmitOrderResponse,
  orderType: BlotterOrderType,
  args: SubmitOrderArgs,
) {
  if (response.type !== 'success') {
    return;
  }
  dispatch(blotterSlice.actions.optimisticOrderCreated(buildOptimisticOrderEntry(response.orderId, orderType, args)));
}

/** Reads a field that only exists on some of the submit request shapes. */
function field<T>(args: SubmitOrderArgs, key: SubmitOrderArgKey): T | null {
  return ((args as Record<string, unknown>)[key] ?? null) as T | null;
}

function toOptionSide(way: ClientWay | null): OptionSide | null {
  if (way === null) {
    return null;
  }
  return way === 'buy' ? 'Buy' : 'Sell';
}

function getExpiry(args: SubmitOrderArgs) {
  if ('expiry' in args) {
    return args.expiry.type === 'GoodTillCancelled'
      ? { isGtc: true, expiryDay: null, expiryTime: null }
      : { isGtc: false, expiryDay: args.expiry.date ?? null, expiryTime: args.expiry.time ?? null };
  }
  if ('expiryDate' in args) {
    return {
      isGtc: args.expiryDate.goodTillDate,
      expiryDay: args.expiryDate.date ?? null,
      expiryTime: args.expiryDate.time ?? null,
    };
  }
  return { isGtc: false, expiryDay: args.expiryDay ?? null, expiryTime: null };
}

function buildOptimisticOrderEntry(
  orderId: string,
  orderType: BlotterOrderType,
  args: SubmitOrderArgs,
): BlotterOrderEntry {
  const { isGtc, expiryDay, expiryTime } = getExpiry(args);

  const notional = field<number>(args, 'notional');
  const notionalCurrency = field<number>(args, 'notionalCurrencyIndex') === 2 ? args.currency2 : args.currency1;
  const liquidityPool = field<string[]>(args, 'liquidityPool');

  return {
    id: orderId,
    updateTime: OPTIMISTIC_UPDATE_TIME,
    clientId: args.clientId,

    orderType,
    status: 'Submitted',

    currencyPair: `${args.currency1}/${args.currency2}`,
    yourWay: toOptionSide(field<ClientWay>(args, 'way')),

    notional,
    notionalCurrency,
    executedAmount: 0,
    remainingAmount: notional,

    marginInBps: field<number>(args, 'commercialMargin'),
    margin: field<number>(args, 'margin'),
    price: null,

    limitPrice: field<number>(args, 'limitPrice') ?? 0,
    customerPrice: field<number>(args, 'customerPrice'),
    executionPrice: null,
    averagePrice: null,
    noWorseThan: null,
    swapPoints: null,
    forwardPrice: null,

    expiryDay,
    expiryTime,
    startTime: null,
    endTime: null,
    maturityDate: field<string>(args, 'tenor') ?? '',

    liquidityPool: liquidityPool?.join(',') ?? null,
    clippingMode: field<string>(args, 'clippingMode'),
    clipSize: field<number>(args, 'clipSize'),
    speed: field<string>(args, 'speed'),
    randomize: field<boolean>(args, 'randomize'),
    alphaSeeker: field<boolean>(args, 'alphaSeeker'),
    spreadCapture: field<boolean>(args, 'spreadCapture'),

    fixingBenchmark: field<string>(args, 'fixingReference'),
    fixingPlace: field<string>(args, 'fixingPlace'),
    fixingTime: field<string>(args, 'fixingTime'),
    fixingDateUtc: field<string>(args, 'fixingDateUtc'),
    fixingPriceType: field<string>(args, 'fixingPriceType'),
    fixingMarginType: field<string>(args, 'fixingMarginType'),

    goodTillCancelled: isAlgoOrderType(orderType) ? false : isGtc,
    isGtc,
    isNdf: field<boolean>(args, 'isNdf') ?? false,
  };
}
